import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from '../../hooks/useDebounce';

import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaClock,
  FaBuilding,
  FaToggleOn,
  FaToggleOff,
  FaCalendarAlt,
  FaFileExcel,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaMapMarkerAlt,
  FaFileInvoice,
  FaPhone,
  FaUser,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';

import {
  FiSearch,
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiRefreshCw,
  FiSettings
} from 'react-icons/fi';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { useAuth } from '../../provider/auth.provider';
import axiosInstance from '../../libs/axios';

import { Link } from 'react-router-dom';
import { SelectWithSearch2, type SelectOption } from "../../utils/select-with-search"

interface ClientRepresentative {
  id: number;
  contact_person: string;
  contact_number: string;
  designation: string;
  representative_location: string;
  client_id: number;
  created_at: string;
  updated_at: string;
}

interface Client {
  id: number;
  client_full_name: string;
  client_short_name: string;

  address: string;
  gst_number: string;
  state: string;
  is_active: boolean;
  created_by: number;
  suspended_by?: number | null;
  suspended_at?: string | null;
  created_at: string;
  updated_at: string;
  representatives?: ClientRepresentative[];
  client_projects?: any[];
  suspended_by_user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  }
  state_circle?: {
    state_name?: string;
    circle_name?: string | null;
  } | null;
}

export interface Filters {
  search?: string;
  name?: string;
  gst_number?: string;
  address?: string;
  status?: string;
  state_id?: string;
  district_id?: string;
  created_from?: string;
  created_to?: string;
  has_representatives?: string;
  has_projects?: string;
  sortBy?: string;
  sortOrder?: string;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onApply: () => void;
  onReset: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedState, setSelectedState] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);

  useEffect(() => {
    if (!filters.state_id) setSelectedState(null);
    if (!filters.district_id) setSelectedDistrict(null);
  }, [filters]);

  if (!isOpen) return null;

  const handleInputChange = (field: keyof Filters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined
    });
  };

  const handleSelectChange = (field: keyof Filters, option: SelectOption | null) => {
    onFiltersChange({
      ...filters,
      [field]: option ? option.value.toString() : undefined
    });
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiSearch },
    // { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'dates', label: 'Dates', icon: FiCalendar },
    { id: 'other', label: 'Other', icon: FiSettings }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Name or GST Number"
                  value={filters.search || ''}
                  onChange={(e) => handleInputChange('search', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Client Name"
                  value={filters.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="GST Number"
                  value={filters.gst_number || ''}
                  onChange={(e) => handleInputChange('gst_number', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Address"
                  value={filters.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State ID</label>
                <SelectWithSearch2
                  placeholder="Select State"
                  value={selectedState}
                  onChange={(option) => {
                    setSelectedState(option);
                    setSelectedDistrict(null);
                    handleSelectChange('state_id', option);
                    handleSelectChange('district_id', null);
                  }}
                  apiEndpoint="/sites/state-circles"
                  dataKey="states"
                  labelKey="state_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">District ID</label>
                <SelectWithSearch2
                  placeholder="Select District"
                  value={selectedDistrict}
                  onChange={(option) => {
                    setSelectedDistrict(option);
                    handleSelectChange('district_id', option);
                  }}
                  apiEndpoint="/sites/state-circles"
                  dataKey="districts"
                  labelKey="name"
                  valueKey="id"
                  searchParam="search"
                  dependencyId={selectedState ? Number(selectedState.value) : null}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ''}
                  onChange={(e) => handleInputChange('created_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ''}
                  onChange={(e) => handleInputChange('created_to', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Other Tab */}
          {activeTab === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Representatives</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_representatives || ''}
                  onChange={(e) => handleInputChange('has_representatives', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Projects</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_projects || ''}
                  onChange={(e) => handleInputChange('has_projects', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply();
                onClose();
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ClientsListProps {
  clients: Client[];
  pagination: PaginationType;
  onSearch: (search: string) => void;
  onFilterChange: (filter: string) => void;
  statusFilter: string;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  onAddClient: () => void;
  onEditClient: (client: Client) => void;
  onDeleteClient: (client: Client) => void;
  onToggleStatus: (client: Client) => void;
  loading?: boolean;
  actionLoading?: boolean;
  onAdvancedFilter?: (filters: Filters) => void;
  onSort?: (field: string, direction: 'ASC' | 'DESC') => void;
  searchValue?: string;
  statusValue?: string;
  appliedFilters?: Filters;
  sortState?: { field: string; direction: 'ASC' | 'DESC' };
}

export const ClientsList: React.FC<ClientsListProps> = ({
  clients,
  pagination,
  onSearch,
  onFilterChange,
  statusFilter,
  onPageChange,
  onLimitChange,
  onAddClient,
  onEditClient,
  onDeleteClient,
  onToggleStatus,
  loading = false,
  actionLoading = false,
  onAdvancedFilter,
  onSort,
  searchValue = '',
  statusValue = 'all',
  appliedFilters,
  sortState
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const { hasPermission } = useAuth();

  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: 'ASC' | 'DESC';
  }>({
    field: 'created_at',
    direction: 'DESC'
  });
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    setSearchTerm(searchValue || '');
    setFilters((prev) => ({ ...prev, search: searchValue || undefined }));
  }, [searchValue]);

  useEffect(() => {
    if (statusValue !== undefined) {
      setFilters((prev) => ({ ...prev, status: statusValue === 'all' ? undefined : statusValue }));
    }
  }, [statusValue]);

  useEffect(() => {
    if (appliedFilters) {
      setFilters((prev) => ({ ...prev, ...appliedFilters }));
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (sortState) {
      setSortConfig(sortState);
    }
  }, [sortState]);

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setFilters(prev => ({ ...prev, search: value }));
    debouncedOnSearch(value);
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (key === 'status') {
      onFilterChange(value);
    }
    if (onAdvancedFilter) {
      onAdvancedFilter(newFilters);
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchTerm('');

    if (onAdvancedFilter) {
      onAdvancedFilter({ search: '', status: '' });
    } else {
      onSearch('');
    }

    onFilterChange('');
  };

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key as keyof Filters]).length;
  };

  const handleApplyFilters = () => {
    if (onAdvancedFilter) {
      onAdvancedFilter(filters);
    }
    if (filters.status !== undefined) {
      onFilterChange(filters.status);
    }
    if (filters.search !== undefined) {
      onSearch(filters.search);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="w-2 h-2" />
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <FaTimesCircle className="w-2 h-2" />
          Suspended
        </span>
      );
    }
  };

  const getEffectiveFilters = (): Filters => ({
    ...(appliedFilters || {}),
    ...filters,
  });

  const getSearchParam = () => {
    const effectiveFilters = getEffectiveFilters();
    return searchValue?.trim() || effectiveFilters.search?.trim() || '';
  };

  const getStatusParam = () => {
    const effectiveFilters = getEffectiveFilters();
    return effectiveFilters.status ?? statusValue;
  };

  const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString() : '');

  const mapClientForExport = (client: Client) => {
    const primaryRep = client.representatives?.[0] as (ClientRepresentative & {
      country_code?: string;
      email?: string | null;
    }) | undefined;
    const suspendedBy = client.suspended_by_user
      ? `${client.suspended_by_user.first_name || ''} ${client.suspended_by_user.last_name || ''}`.trim()
      : '';

    return {
      'Client Name': client.client_full_name,
      'Short Name': client.client_short_name,
      Address: client.address,
      'GST Number': client.gst_number,
      'State Name': client.state_circle?.state_name || client.state || '',
      'Circle Name': client.state_circle?.circle_name || '',
      Status: client.is_active ? 'Active' : 'Suspended',
      'Representatives Count': client.representatives?.length || 0,
      'Projects Count': client.client_projects?.length || 0,
      'Primary Contact Name': primaryRep?.contact_person || '',
      'Primary Contact Phone': primaryRep ? `${(primaryRep as any).country_code || ''} ${primaryRep.contact_number || ''}`.trim() : '',
      'Primary Contact Email': (primaryRep as any)?.email || '',
      'Primary Contact Designation': (primaryRep as any)?.designation?.name || '',
      'Primary Contact Location': primaryRep?.representative_location || '',
      'Created At': formatDateTime(client.created_at),
      'Updated At': formatDateTime(client.updated_at),
      'Suspended At': formatDateTime(client.suspended_at),
      'Suspended By': suspendedBy,
    };
  };

  const buildExportParams = () => {
    const params: Record<string, any> = {};
    const effectiveFilters = getEffectiveFilters();
    const searchParam = getSearchParam();
    if (searchParam) params.search = searchParam;

    const statusParam = getStatusParam();
    if (statusParam && statusParam !== 'all') {
      params.status = statusParam.toLowerCase();
    }

    if (effectiveFilters.name?.trim()) params.name = effectiveFilters.name.trim();
    if (effectiveFilters.gst_number?.trim()) params.gst_number = effectiveFilters.gst_number.trim();
    if (effectiveFilters.address?.trim()) params.address = effectiveFilters.address.trim();
    if (effectiveFilters.state_id?.trim()) params.state_id = effectiveFilters.state_id.trim();
    if (effectiveFilters.district_id?.trim()) params.district_id = effectiveFilters.district_id.trim();
    if (effectiveFilters.created_from) params.created_from = effectiveFilters.created_from;
    if (effectiveFilters.created_to) params.created_to = effectiveFilters.created_to;
    if (effectiveFilters.has_representatives) params.has_representatives = effectiveFilters.has_representatives;
    if (effectiveFilters.has_projects) params.has_projects = effectiveFilters.has_projects;

    const activeSort = sortState || sortConfig;
    if (activeSort?.field) {
      params.sort_by = activeSort.field;
      params.sort_order = activeSort.direction;
    }

    return params;
  };

  const fetchExportData = async () => {
    try {
      setExporting(true);
      const params = buildExportParams();
      const limit = 100;
      let page = 1;
      let totalPages = 1;
      const allClients: Client[] = [];

      do {
        const response = await axiosInstance.get('/clients/list', {
          params: {
            ...params,
            page,
            limit,
          },
        });

        const responseData = response.data?.data;
        const pageClients: Client[] = responseData?.clients || [];
        allClients.push(...pageClients);

        const paginationInfo = responseData?.pagination;
        if (paginationInfo) {
          totalPages =
            paginationInfo.total_pages ??
            paginationInfo.totalPages ??
            (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / limit) : totalPages);
        } else if (pageClients.length < limit) {
          totalPages = page;
        }

        page += 1;
      } while (page <= totalPages);

      setExportData(allClients.map(mapClientForExport));
      setShowExcelModal(true);
    } catch (error) {
      console.error('Error exporting clients:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    setSortConfig({ field, direction });
    onSort?.(field, direction);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />;
    return sortConfig.direction === 'ASC' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />;
  };

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Client Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>

            {hasPermission('Client', 'create') && (
              <button
                onClick={onAddClient}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add New Client
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Quick Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Quick search by Name, GST or Address..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.search || ''}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {/* Advanced Filter Button */}
          <div className="w-full sm:w-auto">
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Active Filters Display */}
        {getActiveFiltersCount() > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Active Filters:</span>
              {Object.entries(filters).map(([key, value]) =>
                value && (
                  <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {key.replace(/_/g, ' ')}: {value}
                    <button
                      onClick={() => {
                        const newFilters = { ...filters };
                        delete newFilters[key as keyof Filters];
                        setFilters(newFilters);
                        const updatedFilters = { ...newFilters };

                        if (key === 'status') {
                          updatedFilters.status = '';
                        }

                        if (key === 'search') {
                          setSearchTerm('');
                          updatedFilters.search = '';
                        }

                        if (onAdvancedFilter) {
                          onAdvancedFilter(updatedFilters);
                        } else if (key === 'search') {
                          onSearch('');
                        }

                        if (key === 'status') {
                          onFilterChange('');
                        }
                      }}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </span>
                )
              )}
              <button
                onClick={handleResetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('client_full_name')}
              >
                <div className="flex items-center gap-1">
                  Full Name
                  {getSortIcon('client_full_name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('client_short_name')}
              >
                <div className="flex items-center gap-1">
                  Short Name
                  {getSortIcon('client_short_name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('address')}
              >
                <div className="flex items-center gap-1">
                  Address
                  {getSortIcon('address')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('gst_number')}
              >
                <div className="flex items-center gap-1">
                  GST Number
                  {getSortIcon('gst_number')}
                </div>
              </th>

              {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <div className="space-y-2">
                  <div>State</div>
                </div>
              </th> */}
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('is_active')}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon('is_active')}
                </div>
              </th>

              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <div className="space-y-2">
                  <div>Representatives</div>
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('suspended_at')}
              >
                <div className="flex items-center gap-1">
                  Suspended At
                  {getSortIcon('suspended_at')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={14} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading clients...</span>
                  </div>
                </td>
              </tr>
            ) : clients.length === 0 ? (
              <tr>
                <td colSpan={14} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaBuilding className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No clients found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new client</p>
                  </div>
                </td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission('Client', 'edit') && (
                        <button
                          onClick={() => onToggleStatus(client)}
                          disabled={actionLoading}
                          className={`p-2 rounded transition-colors ${client.is_active
                            ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'
                            : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                            } ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={client.is_active ? 'Suspend client' : 'Activate client'}
                        >
                          {client.is_active ? (
                            <FaToggleOn className="w-4 h-4" />
                          ) : (
                            <FaToggleOff className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {hasPermission('Client', 'view') &&
                        <Link
                          target="_blank"
                          to={`/clients/${client.id}`}
                          className={`text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="View client"
                        >
                          <FaEye className="w-4 h-4" />
                        </Link>
                      }
                      {hasPermission('Client', 'edit') &&
                        <button
                          onClick={() => onEditClient(client)}
                          disabled={actionLoading}
                          className={`text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="Edit client"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      }
                      {/* {hasPermission('Client', 'delete') && (
                        <button
                          onClick={() => onDeleteClient(client)}
                          disabled={actionLoading}
                          className={`text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="Delete client"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )} */}
                    </div>
                  </td>

                  {/* ID */}
                  {/* <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 font-medium">{client.id}</span>
                  </td> */}

                  {/* Full Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {client.client_full_name}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Short Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-blue-600 font-medium">
                      {client.client_short_name}
                    </span>
                  </td>

                  {/* Address */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-600 truncate max-w-[200px]">
                      {client.address}
                    </div>
                  </td>

                  {/* GST Number */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <span className="font-mono">{client.gst_number}</span>
                    </div>
                  </td>

                  {/* State */}
                  {/* <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 font-medium">{client.state}</span>
                  </td> */}

                  {/* Status */}
                  <td className="flex flex-col justify-center items-centerpx-4 py-4 whitespace-nowrap">
                    {getStatusBadge(client.is_active)}
                    {client.suspended_by_user && <span className='ml-1 text-xs text-gray-600'>~ by {client?.suspended_by_user?.first_name + ' ' + client?.suspended_by_user?.last_name || 'N/A'}</span>}
                  </td>

                  {/* Representatives */}
                  <td className="px-4 py-4">
                    <div className="space-y-1">
                      {client.representatives && client.representatives.length > 0 ? (
                        <>
                          <div className="flex items-center gap-1 text-sm text-gray-900">
                            <span className="font-medium">{client.representatives.length}</span>
                          </div>
                          <div className="space-y-1">
                            {client.representatives.slice(0, 1).map((rep, index) => (
                              <div key={index} className="text-xs text-gray-600">
                                <div className="flex items-center gap-1">
                                  <FaUser className="w-2 h-2" />
                                  <span className="truncate max-w-[100px]">{rep.contact_person}</span>
                                </div>
                              </div>
                            ))}
                            {client.representatives.length > 1 && (
                              <div className="text-xs text-gray-400">
                                +{client.representatives.length - 1} more
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-gray-400 italic">
                          No representatives
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <span>{formatDate(client.created_at)}</span>
                    </div>
                  </td>

                  {/* Suspended At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {client.suspended_at ? formatDate(client.suspended_at) : '-'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        pagination={pagination}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="clients"
        title="Export Clients Data"
        description="Download the filtered client records with all available details"
      />
    </div>
  );
};
